import { useState, useEffect, useCallback, Fragment, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Assistant, Laterality as TemplateLaterality } from '@/lib/templates/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Check, AlertTriangle, FileText, Send, Printer, Lock,
  Loader2, RefreshCw, Shield, Clock, DollarSign,
  User, Calendar, MapPin, Hash, ArrowLeft, Eye, Play, Volume2, Square, CheckCircle, Sparkles,
} from 'lucide-react';
import { ScheduledCase, SurgeonTemplate, CaseCptLine, Laterality, SurgeonFacility } from '@/lib/templates/types';
import { MODIFIER_DESCRIPTIONS } from '@/lib/modifiers/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoPath } from '@/contexts/DemoPathContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { InlineResolutionField, ResolutionFieldConfig } from './smart-builder/review/InlineResolutionField';
import { useSpeechSynthesis } from '@/hooks/useSpeechSynthesis';
import { cn } from '@/lib/utils';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import { crossReferenceCptEvidence, filterSupportedCptLines } from '@/lib/cptEvidenceCrossRef';
import { evaluateOrthopedicCase } from '@/lib/denialDefenderRules';
import { SurgeonConversationPanel } from './SurgeonConversationPanel';
import {
  buildPreAuthProofPacket,
  type ClinicalQuestion,
} from '@/lib/surgeon-console/proofPacket';
import { buildPreAuthAuthoringAudit } from '@/lib/surgeon-console/preAuthAuthoringBrain';
import { buildPayerFacingClinicalJustification } from '@/lib/surgeon-console/payerFacingNarrative';
import {
  authorizationLinkedDiagnoses,
  buildFinalizedAuthorizationBundle,
  buildRequestedProcedureSection,
  createOfficeManagerFlag,
  normalizeCptLines,
  saveFinalizedAuthorizationBundle,
  saveOfficeManagerFlag,
  validatePacketMatchesCodeCart,
  type OfficeManagerFlag,
} from '@/lib/surgeon-console/finalizedAuthorization';
import { hasMatchingAttachment } from '@/lib/surgeon-console/attachmentMatcher';
import { markDemoPreAuthSigned } from '@/lib/demo/demoPresentationState';
import { shouldShowSurgeryRevenueForDemoPath } from '@/lib/demo/demoPathVisibility';
import { isPerfectWorldDemo, normalizeDemoPreAuthResult } from '@/lib/demo/perfectWorldDemo';
import { playSentHandoffChime, SentHandoffOverlay } from './smart-builder/review/SentHandoffOverlay';

interface AuthorizationStepProps {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  laterality: Laterality | null;
  facility: SurgeonFacility | null;
  cptLines: CaseCptLine[];
  renderedNote: string;
  onFinalize: () => void;
  isFinalized: boolean;
  highComplexity: boolean;
  onHighComplexityChange: (val: boolean) => void;
  bmiValue?: number;
  onBmiValueChange?: (val: number) => void;
  assistantNoteEnabled?: boolean;
  selectedAssistant?: Assistant | null;
  /** AI-extracted clinical context from uploaded documents */
  clinicalContext?: string | null;
  /** Structured clinical severity data for evidence cross-referencing */
  clinicalSeverityData?: ClinicalSeverityData | null;
  onDemoPreAuthSigned?: (caseId: string) => void;
}

interface AuthNarrativeResult {
  narrative: string;
  primary_cpt: string;
  icd10_codes: string[];
  payer_specific_notes: string;
  required_attachments: string[];
  documentation_gaps: string[];
  approval_likelihood: number;
  approval_likelihood_label: string;
  expected_timeline: string;
  revenue_impact: string;
  disclaimer: string;
  pt_start_date: string | null;
  pt_end_date: string | null;
  last_injection_date: string | null;
  surgery_date: string | null;
}

const cptCode = (line: Pick<CaseCptLine, 'code'>) => String(line.code);

function buildDeterministicAuthNarrative(params: {
  selectedCase: ScheduledCase;
  safeCptLines: CaseCptLine[];
  diagnosisCodes: { code: string; description: string }[];
  payerFacingNarrative: string;
}): AuthNarrativeResult {
  const { selectedCase, safeCptLines, diagnosisCodes, payerFacingNarrative } = params;
  const primaryLine = safeCptLines[0];
  const attachments = selectedCase.pre_loaded_docs?.length
    ? selectedCase.pre_loaded_docs.map((doc) => doc.type)
    : ['Clinical packet', 'Imaging or diagnostic report', 'Signed physician order'];
  const icd10Codes = diagnosisCodes
    .map((diagnosis) => diagnosis.code)
    .filter(Boolean);

  return {
    narrative: payerFacingNarrative,
    primary_cpt: primaryLine?.code || '',
    icd10_codes: icd10Codes,
    payer_specific_notes:
      'Deterministic demo packet generated from pre-loaded synthetic documents because the narrative service was unavailable.',
    required_attachments: attachments,
    documentation_gaps: [],
    approval_likelihood: 94,
    approval_likelihood_label: 'Demo packet ready',
    expected_timeline: 'Ready for payer submission after surgeon review',
    revenue_impact: 'Preserves requested authorized CPT universe with documented laterality and packet evidence.',
    disclaimer: 'Synthetic demo packet. Final production packet must be reviewed and signed by the operating surgeon.',
    pt_start_date: null,
    pt_end_date: null,
    last_injection_date: null,
    surgery_date: selectedCase.scheduled_date,
  };
}

// ── Payer threshold rules (mock data for prototype) ───────────────
const KNEE_CPTS = ['27447', '27130', '27570'];
const SHOULDER_CPTS = ['29827', '23472', '23470', '23410', '23412', '29806', '29884'];

function getPayerThreshold(romCpts: string[]): { threshold: string; attestationText: string } {
  const hasKnee = romCpts.some(c => KNEE_CPTS.includes(c));
  const hasShoulder = romCpts.some(c => SHOULDER_CPTS.includes(c));
  if (hasKnee && hasShoulder) {
    return {
      threshold: 'Flexion < 90° or Extension deficit > 10°; Active Forward Elevation < 90°',
      attestationText: 'Patient exhibits severely restricted ROM with flexion < 90°, extension deficit > 10°, and active forward elevation < 90°.',
    };
  }
  if (hasKnee) {
    return {
      threshold: 'Flexion < 90° or Extension deficit > 10°',
      attestationText: 'Patient exhibits severely restricted ROM with flexion < 90° and extension deficit > 10°.',
    };
  }
  return {
    threshold: 'Active Forward Elevation < 90°',
    attestationText: 'Patient exhibits severely restricted ROM with active forward elevation < 90°.',
  };
}

// ── ROM Attestation inline component ──────────────────────────────
function InlineRomAttestation({
  value,
  onSave,
  romCpts,
  highlighted,
  aiVerified,
}: {
  value: string;
  onSave: (val: string) => void;
  romCpts: string[];
  highlighted?: boolean;
  /** If true, ROM was auto-verified from AI extraction */
  aiVerified?: boolean;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { threshold, attestationText } = getPayerThreshold(romCpts);

  useEffect(() => {
    if (showManualInput && inputRef.current) inputRef.current.focus();
  }, [showManualInput]);

  // Resolved state
  if (value) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold cursor-pointer border rounded-md px-2 sm:px-2.5 py-1 transition-all duration-300 mx-0.5",
          aiVerified
            ? "bg-primary/10 text-primary border-primary/30 hover:bg-primary/20"
            : "bg-primary/10 text-primary border-primary/30 hover:bg-primary/20"
        )}
        onClick={() => {
          onSave('');
          setShowManualInput(false);
          setDraft('');
        }}
        title="Click to undo"
      >
        {aiVerified ? (
          <>
            <Sparkles className="w-3.5 h-3.5" />
            ROM Auto-Verified (AI Extracted)
          </>
        ) : (
          '✓ ROM Verified'
        )}
        <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-primary" />
      </span>
    );
  }

  // Manual input mode
  if (showManualInput) {
    return (
      <span className="inline-flex items-center gap-1 align-baseline mx-0.5" data-compliance-gap="rom_required">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && draft.trim()) {
              onSave(draft.trim());
              setShowManualInput(false);
              setDraft('');
            }
          }}
          onBlur={() => {
            if (draft.trim()) {
              onSave(draft.trim());
              setShowManualInput(false);
              setDraft('');
            }
          }}
          placeholder="✎ Enter exact ROM (e.g., Flexion 85°)..."
          className="inline-block w-64 h-7 px-2 text-sm border-2 border-amber-400 rounded-md bg-amber-50 text-foreground placeholder:text-amber-400/70 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        />
        <button
          onClick={() => {
            if (draft.trim()) {
              onSave(draft.trim());
              setShowManualInput(false);
              setDraft('');
            }
          }}
          disabled={!draft.trim()}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
          title="Save ROM"
        >
          <Check className="w-4 h-4" />
        </button>
      </span>
    );
  }

  // Default: attestation popover trigger with pulsating sparkle
  return (
    <span className={cn(
      "inline-flex items-center gap-1 align-baseline mx-0.5 transition-all duration-500",
      highlighted && !value && "rounded-lg ring-2 ring-amber-400 ring-offset-2 ring-offset-background bg-amber-50/60 px-1 py-0.5"
    )} data-compliance-gap="rom_required">
      {/* highlight ring only */}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer border whitespace-normal text-left',
              'rounded-md px-2 sm:px-2.5 py-1',
              'bg-muted text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground',
            )}
          >
            ✓ Verify: ROM Required
            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-muted-foreground/40" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0" sideOffset={6}>
          <div className="p-3 space-y-3">
            <p className="text-xs text-muted-foreground leading-snug">
              Payer requirement for medical necessity:
            </p>
            <div className="space-y-2">
              <button
                onClick={() => {
                  onSave(attestationText);
                  setPopoverOpen(false);
                }}
                className="w-full text-left px-3 py-2.5 rounded-md border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-colors"
              >
                <span className="text-sm font-semibold text-primary">
                  Attest: {threshold}
                </span>
              </button>
              <button
                onClick={() => {
                  setPopoverOpen(false);
                  setTimeout(() => setShowManualInput(true), 150);
                }}
                className="w-full text-left px-3 py-2 rounded-md border border-border hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <span className="text-sm font-medium text-muted-foreground">
                  Other (Specify Exact ROM)
                </span>
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <Sparkles className="w-3.5 h-3.5 text-amber-500 animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
    </span>
  );
}

// ── Pre-Auth Resolution Field Configs ──────────────────────────────

function buildPreAuthFields(cptLines: CaseCptLine[], hasGraftCpt: boolean): ResolutionFieldConfig[] {
  const fields: ResolutionFieldConfig[] = [];

  // Order matches top-to-bottom document flow

  if (hasGraftCpt) {
    fields.push({
      id: 'graft_type',
      label: 'Graft Type',
      placeholder: 'Verify Graft Type',
      assistText: 'CPT 20900 requires specification of graft type (autograft vs. allograft) for payer authorization.',
      options: ['Autograft', 'Allograft'],
      defaultValue: 'Autograft',
    });
  }

  // patient_sex is no longer a verification field — it flows from Step 1 patient intake

  fields.push({
    id: 'laterality',
    label: 'Laterality',
    placeholder: 'Verify Laterality',
    assistText: 'Laterality must be explicitly documented for authorization of unilateral procedures.',
    options: ['RIGHT', 'LEFT', 'BILATERAL'],
    defaultValue: 'RIGHT',
  });

  return fields;
}

// ── Pre-Auth Note Template ─────────────────────────────────────────

function calculateAgeFromDob(dobStr: string): number | null {
  if (!dobStr) return null;
  // Parse as local date to avoid timezone shift
  const parts = dobStr.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (parts) {
    const [, m, d, y] = parts;
    const birth = new Date(Number(y), Number(m) - 1, Number(d));
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  }
  // Try YYYY-MM-DD format
  const isoParts = dobStr.match(/(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
  if (isoParts) {
    const [, y, m, d] = isoParts;
    const birth = new Date(Number(y), Number(m) - 1, Number(d));
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  }
  return null;
}

function buildPreAuthNoteTemplate(
  patientName: string,
  patientDob: string,
  patientMrn: string,
  surgeonName: string,
  payerName: string,
  facilityName: string,
  scheduledDate: string,
  requestedProcedureText: string,
  diagnosisCodes: { code: string; description: string }[],
  hasGraftCpt: boolean,
  patientSex?: string | null,
) {
  const dateStr = scheduledDate
    ? format(new Date(scheduledDate), 'MM/dd/yyyy')
    : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

  const calculatedAge = calculateAgeFromDob(patientDob);

  const diagnosisText = diagnosisCodes.length > 0
    ? diagnosisCodes.map((d, i) => `${i + 1}. ${d.description} (${d.code})`).join('\n')
    : '1. [Diagnosis not specified — please add diagnosis codes in Step 1]';

  const graftSection = hasGraftCpt
    ? `\n\nBone graft type: {{graft_type}}. This has been confirmed by the operating surgeon and is documented for payer review.`
    : '';

  return `Patient: ${patientName}
Date of Birth: ${patientDob}
MRN: ${patientMrn}
Date of Service: ${dateStr}

Location: ${facilityName}
Visit Type: Pre-Operative Authorization
Provider: ${surgeonName}
Payer: ${payerName}

Purpose: Pre-Op Authorization Request

Diagnosis:
${diagnosisText}

Procedure Requested:
${requestedProcedureText}

Clinical Justification:

{{clinical_justification}}
${graftSection}`;
}

// ── Render note with inline fields ─────────────────────────────────

function renderNoteWithFields(
  template: string,
  fields: ResolutionFieldConfig[],
  values: Record<string, string>,
  onResolve: (id: string, val: string) => void,
  onSaveToTemplate?: (id: string, val: string) => void,
  romValue?: string,
  onRomSave?: (val: string) => void,
  romCpts?: string[],
  highlightedGapId?: string | null,
  highReuseFields?: Set<string>,
  romAiVerified?: boolean,
) {
  const fieldMap = Object.fromEntries(fields.map(f => [f.id, f]));
  const parts = template.split(/(\{\{[a-z_]+\}\}|\[GAP:\s*ROM_REQUIRED\])/g);

  return parts.map((part, i) => {
    const fieldMatch = part.match(/^\{\{([a-z_]+)\}\}$/);
    if (fieldMatch && fieldMap[fieldMatch[1]]) {
      const config = fieldMap[fieldMatch[1]];
      return (
        <InlineResolutionField
          key={i}
          config={config}
          value={values[config.id] ?? ''}
          onChange={onResolve}
          onSaveToTemplate={highReuseFields?.has(config.id) ? onSaveToTemplate : undefined}
          highlighted={highlightedGapId === config.id}
        />
      );
    }
    if (/^\[GAP:\s*ROM_REQUIRED\]$/.test(part) && onRomSave) {
      return (
        <InlineRomAttestation
          key={i}
          value={romValue || ''}
          onSave={onRomSave}
          romCpts={romCpts || []}
          highlighted={highlightedGapId === 'rom_required'}
          aiVerified={romAiVerified}
        />
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

// ── Compliance Gate Banner ─────────────────────────────────────────

function PreAuthComplianceGate({
  fields,
  resolvedValues,
  onStartVerification,
  hasRomRequirement,
  romResolved,
  romAiVerified,
  authoringGaps,
  proofGaps,
}: {
  fields: ResolutionFieldConfig[];
  resolvedValues: Record<string, string>;
  onStartVerification: () => void;
  hasRomRequirement?: boolean;
  romResolved?: boolean;
  romAiVerified?: boolean;
  authoringGaps?: Array<{ id: string; label: string; resolved: boolean; aiVerified?: boolean }>;
  proofGaps?: Array<{ id: string; label: string; resolved: boolean; aiVerified?: boolean }>;
}) {
  const gaps: Array<{ id: string; label: string; resolved: boolean; aiVerified?: boolean }> = fields.map(f => ({
    id: f.id,
    label: `Verify ${f.label} Required`,
    resolved: (resolvedValues[f.id] ?? '').trim().length > 0,
    aiVerified: false,
  }));

  if (hasRomRequirement) {
    gaps.push({
      id: 'rom_required',
      label: romAiVerified ? 'ROM Auto-Verified (AI Extracted)' : 'Verify Range of Motion (ROM)',
      resolved: !!romResolved,
      aiVerified: !!romAiVerified,
    });
  }
  if (authoringGaps?.length) {
    gaps.push(...authoringGaps);
  }
  if (proofGaps?.length) {
    gaps.push(...proofGaps);
  }

  const unresolvedCount = gaps.filter(g => !g.resolved).length;
  const allResolved = unresolvedCount === 0;

  if (gaps.length === 0) return null;

  // All resolved — green banner
  if (allResolved) {
    return (
      <div className="rounded-lg border-2 border-success/30 bg-success/5 p-4 sm:p-5 transition-all duration-500 animate-fade-in">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-success/10">
            <CheckCircle className="w-5 h-5 text-success" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-success tracking-tight">Ready for Surgeon Review — Documentation Gaps Resolved</h4>
            <p className="text-xs text-success/70 mt-0.5">
              Pre-Op Authorization verified against internal documentation requirements. Final signer review is still required.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Unresolved — blue banner with bullet list + Start Verification
  return (
    <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4 sm:p-5 transition-all duration-300">
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 shrink-0 mt-0.5">
          <Shield className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-primary tracking-tight">
            Compliance Gate — Signature Blocked
          </h4>
          <p className="text-xs text-primary/80 mt-1">
            {unresolvedCount} documentation gap{unresolvedCount !== 1 ? 's' : ''} must be resolved before signing.
          </p>

          <ul className="mt-3 space-y-2">
            {gaps.map((gap) => (
              <li
                key={gap.id}
                className={cn(
                  'flex items-center gap-2.5 text-sm transition-all duration-400',
                  gap.resolved ? 'text-success' : 'text-primary',
                )}
              >
                {gap.resolved ? (
                  gap.aiVerified ? (
                    <Sparkles className="w-4 h-4 text-success shrink-0" />
                  ) : (
                    <CheckCircle className="w-4 h-4 text-success shrink-0" />
                  )
                ) : (
                  <span className="relative flex items-center justify-center w-4 h-4 shrink-0">
                    <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                    <span className="absolute w-2.5 h-2.5 rounded-full bg-primary animate-ping opacity-40" />
                  </span>
                )}
                <span className={cn(
                  'font-medium',
                  gap.resolved && !gap.aiVerified && 'line-through opacity-60',
                )}>
                  {gap.label}
                </span>
              </li>
            ))}
          </ul>

          <Button
            onClick={onStartVerification}
            size="sm"
            className="mt-4 h-10 px-6 text-sm font-semibold gap-2 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md"
          >
            <Play className="w-4 h-4" />
            Start Verification
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────

export function AuthorizationStep({
  selectedCase,
  template,
  laterality,
  facility,
  cptLines,
  renderedNote,
  onFinalize,
  isFinalized,
  assistantNoteEnabled,
  selectedAssistant,
  clinicalContext,
  clinicalSeverityData,
  onDemoPreAuthSigned,
}: AuthorizationStepProps) {
  const navigate = useNavigate();
  const { profile } = useAuthContext();
  const { isDemoMode } = useDemoMode();
  const { demoPath } = useDemoPath();
  const showRevenueImpact = shouldShowSurgeryRevenueForDemoPath({ demoPath });
  const perfectWorldDemo = isPerfectWorldDemo(isDemoMode, selectedCase.id);
  const { isSpeaking, handleSpeak } = useSpeechSynthesis();
  const [result, setResult] = useState<AuthNarrativeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSigned, setIsSigned] = useState(false);
  const [checkedAttachments, setCheckedAttachments] = useState<Record<string, boolean>>({});
  const [complianceBlock, setComplianceBlock] = useState<string | null>(null);
  const [showFullReview, setShowFullReview] = useState(false);
  const [resolvedValues, setResolvedValues] = useState<Record<string, string>>({});
  const resolvedValuesRef = useRef<Record<string, string>>({});
  const [romValue, setRomValue] = useState('');
  const romValueRef = useRef('');
  const [romAiVerified, setRomAiVerified] = useState(false);
  const [highlightedGapId, setHighlightedGapId] = useState<string | null>(null);
  const [conversationStatements, setConversationStatements] = useState<string[]>([]);
  const [showDemoSentOverlay, setShowDemoSentOverlay] = useState(false);
  const normalizePreAuthResultForDemo = useCallback((next: AuthNarrativeResult): AuthNarrativeResult => (
    perfectWorldDemo ? normalizeDemoPreAuthResult(next, selectedCase.id) : next
  ), [perfectWorldDemo, selectedCase.id]);

  const ROM_REQUIRED_CPTS = ['27447', '27130', '23472', '23470', '27570', '29884', '29827', '23410', '23412', '29806'];
  const romRequiredCpts = useMemo(
    () => cptLines.filter(l => ROM_REQUIRED_CPTS.includes(cptCode(l))).map(cptCode),
    [cptLines],
  );
  const hasRomRequirement = romRequiredCpts.length > 0;
  const templateSurgeonName = typeof template.metadata?.operating_surgeon_name === 'string'
    ? template.metadata.operating_surgeon_name
    : null;
  const surgeonName = templateSurgeonName || (profile?.full_name
    ? (profile.full_name.startsWith('Dr.') ? profile.full_name : `Dr. ${profile.full_name}`)
    : 'Surgeon');
  const authorizationFacilityName = facility?.facility_name || selectedCase.facility_name || 'N/A';

  const packetClinicalText = useMemo(() => {
    const docText = (selectedCase.pre_loaded_docs || [])
      .map(doc => [doc.type, doc.extracted_text].filter(Boolean).join(': '))
      .join('\n\n');
    return [clinicalContext, renderedNote, docText].filter(Boolean).join('\n\n');
  }, [clinicalContext, renderedNote, selectedCase.pre_loaded_docs]);
  const authoringClinicalText = useMemo(
    () => [packetClinicalText, ...conversationStatements].filter(Boolean).join('\n\n'),
    [packetClinicalText, conversationStatements],
  );

  const proofPacket = useMemo(() => buildPreAuthProofPacket({
    selectedCase,
    template,
    cptLines,
    laterality,
    clinicalText: authoringClinicalText,
    clinicalSeverityData,
    assistantNoteEnabled: false,
  }), [
    selectedCase,
    template,
    cptLines,
    laterality,
    authoringClinicalText,
    clinicalSeverityData,
  ]);

  const authoringAudit = useMemo(() => buildPreAuthAuthoringAudit({
    selectedCase,
    template,
    cptLines,
    laterality,
    clinicalText: authoringClinicalText,
    clinicalSeverityData,
    surgeonName,
  }), [selectedCase, template, cptLines, laterality, authoringClinicalText, clinicalSeverityData, surgeonName]);

  const handleAcceptConversationStatement = useCallback((statement: string, question: ClinicalQuestion) => {
    const trimmed = statement.trim();
    if (!trimmed) return;
    setConversationStatements(prev => {
      if (prev.some(existing => existing.trim().toLowerCase() === trimmed.toLowerCase())) return prev;
      return [...prev, trimmed];
    });
  }, []);

  // ── ROM Auto-Verification from structured extraction or raw packet text ──
  useEffect(() => {
    if (!hasRomRequirement || romValue) return; // already resolved or not needed

    const rom = clinicalSeverityData?.rom_measurements;
    const hasKnee = romRequiredCpts.some(c => KNEE_CPTS.includes(c));
    const hasShoulder = romRequiredCpts.some(c => SHOULDER_CPTS.includes(c));
    const hasKneeManipulation = romRequiredCpts.includes('27570');

    const parts: string[] = [];

    if (hasKnee && rom) {
      // Check flexion
      const flexion = rom.flexion;
      if (flexion) {
        const match = flexion.match(/(\d+)/);
        if (match) {
          const deg = parseInt(match[1]);
          if (deg <= 90 || (hasKneeManipulation && deg <= 100)) parts.push(`Flexion ${deg}°`);
        }
      }
      // Check extension
      const extension = rom.extension;
      if (extension) {
        const match = extension.match(/(\d+)/);
        if (match) {
          const deg = parseInt(match[1]);
          // Extension deficit > 10° means extension value > 10
          if (deg >= 10) parts.push(`Extension deficit ${deg}°`);
        }
      }
    }

    if (hasKneeManipulation && clinicalSeverityData?.knee_mua) {
      const flexion = clinicalSeverityData.knee_mua.rom_flexion;
      const extension = clinicalSeverityData.knee_mua.rom_extension;
      if (flexion && parts.every(part => !/flexion/i.test(part))) {
        const range = flexion.match(/(\d{2,3})\s*(?:to|-)\s*(\d{2,3})/i);
        const single = flexion.match(/(\d{2,3})/);
        if (range) {
          const high = parseInt(range[2], 10);
          if (Number.isFinite(high) && high <= 100) parts.push(`Flexion plateau ${range[1]}°-${range[2]}°`);
        } else if (single) {
          const deg = parseInt(single[1], 10);
          if (Number.isFinite(deg) && deg <= 100) parts.push(`Flexion ${deg}°`);
        }
      }
      if (extension && parts.every(part => !/extension/i.test(part))) {
        const match = extension.match(/(\d{1,3})/);
        if (match) {
          const deg = parseInt(match[1], 10);
          if (Number.isFinite(deg)) parts.push(`Extension ${deg}°`);
        }
      }
    }

    if (hasShoulder && rom) {
      // Check forward elevation / flexion for shoulder
      const allMeasurements = [rom.flexion, ...(rom.other_measurements || [])].filter(Boolean) as string[];
      for (const m of allMeasurements) {
        const lower = m.toLowerCase();
        if (lower.includes('forward') || lower.includes('elevation') || lower.includes('flexion')) {
          const match = m.match(/(\d+)/);
          if (match) {
            const deg = parseInt(match[1]);
            if (deg <= 90) {
              parts.push(`Active forward elevation ${deg}°`);
              break;
            }
          }
        }
      }
    }

    if (hasKneeManipulation && parts.length === 0) {
      const text = packetClinicalText || '';
      const flexionRange = text.match(/flexion[^.\n]{0,100}?(\d{2,3})\s*(?:to|-)\s*(\d{2,3})\s*degrees/i);
      const flexionSingle = text.match(/flexion[^.\n]{0,100}?(\d{2,3})\s*degrees/i);
      const extension = text.match(/extension[^.\n]{0,80}?(\d{1,3})\s*degrees/i);
      if (flexionRange) {
        const low = parseInt(flexionRange[1], 10);
        const high = parseInt(flexionRange[2], 10);
        if (Number.isFinite(low) && Number.isFinite(high) && high <= 100) {
          parts.push(`Flexion plateau ${low}°-${high}°`);
        }
      } else if (flexionSingle) {
        const deg = parseInt(flexionSingle[1], 10);
        if (Number.isFinite(deg) && deg <= 100) {
          parts.push(`Flexion ${deg}°`);
        }
      }
      if (extension) {
        const deg = parseInt(extension[1], 10);
        if (Number.isFinite(deg)) {
          parts.push(`Extension ${deg}°`);
        }
      }
    }

    if (parts.length > 0) {
      const attestation = `Patient exhibits documented ROM limitation: ${parts.join(', ')}. (Auto-verified from clinical evidence)`;
      setRomValue(attestation);
      romValueRef.current = attestation;
      setRomAiVerified(true);
    }
  }, [hasRomRequirement, clinicalSeverityData, romRequiredCpts, packetClinicalText, romValue]);

  const primaryLines = cptLines.filter(l => l.section === 'primary');
  const secondaryLines = cptLines.filter(l => l.section === 'secondary');
  const hasGraftCpt = cptLines.some(l => cptCode(l) === '20900');
  const normalizedCptLines = useMemo(
    () => normalizeCptLines(cptLines, laterality),
    [cptLines, laterality],
  );

  const resolutionFields = buildPreAuthFields(cptLines, hasGraftCpt);

  // Keep refs in sync with state
  useEffect(() => {
    resolvedValuesRef.current = resolvedValues;
  }, [resolvedValues]);
  useEffect(() => {
    romValueRef.current = romValue;
  }, [romValue]);

  useEffect(() => {
    if (laterality && !resolvedValues.laterality) {
      setResolvedValues(prev => {
        const next = { ...prev, laterality: laterality.toUpperCase() };
        resolvedValuesRef.current = next;
        return next;
      });
    }
  }, [laterality]);

  // Do NOT seed clinical_justification from clinicalContext (status logs).
  // Only the AI narrative from generate-rcm-narrative should populate this field.

  useEffect(() => {
    const narrative = result?.narrative?.trim();
    if (!narrative) return;

    // AI narrative ALWAYS wins — overwrite any previous value including clinicalContext
    setResolvedValues(prev => {
      const next = { ...prev, clinical_justification: narrative };
      resolvedValuesRef.current = next;
      return next;
    });
  }, [result?.narrative]);

  const authoringGateResolved = authoringAudit.status !== 'blocked' && authoringAudit.surgeonQuestions.length === 0;
  const effectiveAuthoringGateResolved = perfectWorldDemo || authoringGateResolved;
  const proofCriteriaResolved = proofPacket.criteria.every(item =>
    item.status === 'satisfied' || item.status === 'not_applicable',
  );
  const effectiveProofCriteriaResolved = perfectWorldDemo || proofCriteriaResolved;
  const preAuthHardBlocked = perfectWorldDemo ? false : authoringAudit.status === 'blocked' || proofPacket.readinessStatus === 'blocked';
  const allClinicalFieldsResolved = resolutionFields.every(f => (resolvedValues[f.id] ?? '').trim().length > 0)
    && (!hasRomRequirement || romValue.trim().length > 0)
    && effectiveAuthoringGateResolved
    && effectiveProofCriteriaResolved;
  const proofComplianceGaps = proofPacket.criteria
    .filter(item => item.status !== 'satisfied' && item.status !== 'not_applicable')
    .map(item => ({
      id: `proof-${item.id}`,
      label: item.label,
      resolved: false,
      aiVerified: false,
    }));
  const authoringComplianceGaps = authoringAudit.items
    .filter(item => item.severity === 'blocker' || item.severity === 'ask_surgeon')
    .map(item => ({
      id: item.id,
      label: item.title,
      resolved: false,
      aiVerified: false,
    }));

  // Fields that have high reuse probability (same answer most of the time)
  const HIGH_REUSE_FIELDS = new Set(['graft_type']);

  const handleResolve = (id: string, value: string) => {
    const updatedValues = { ...resolvedValuesRef.current, [id]: value };
    resolvedValuesRef.current = updatedValues;
    setResolvedValues(updatedValues);
    setHighlightedGapId(null);

    // Auto-scroll to next unresolved gap after 400ms (DocuSign guided tour)
    // Uses DOM order (top-to-bottom) to guarantee document-flow sequencing
    if (value.trim()) {
      setTimeout(() => {
        const currentValues = resolvedValuesRef.current;
        const currentRom = romValueRef.current;
        const allGapEls = Array.from(document.querySelectorAll<HTMLElement>('[data-compliance-gap]'));
        let foundNext = false;
        for (const el of allGapEls) {
          const gapId = el.getAttribute('data-compliance-gap');
          if (!gapId) continue;
          const isResolved = gapId === 'rom_required'
            ? currentRom.trim().length > 0
            : (currentValues[gapId] ?? '').trim().length > 0;
          if (!isResolved) {
            setHighlightedGapId(gapId);
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            foundNext = true;
            break;
          }
        }
        if (!foundNext) {
          // All gaps resolved — scroll to Sign & Submit button
          const signBtn = document.querySelector('[data-sign-button]');
          if (signBtn) {
            signBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 400);
    }
  };

  const handleSaveToTemplate = useCallback(async (fieldId: string, value: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const surgeonId = user?.id || 'demo-surgeon';
      const preferenceKey = `preauth_default_${fieldId}`;
      const { error: upsertError } = await supabase
        .from('surgeon_template_versions' as any)
        .upsert({
          template_id: template?.id || 'default',
          surgeon_id: surgeonId,
          field_key: preferenceKey,
          default_value: value,
          updated_at: new Date().toISOString(),
        } as any, { onConflict: 'template_id,surgeon_id,field_key' })
        .select();
      console.log('Template preference save attempted:', { fieldId, value, error: upsertError?.message });
      toast.success('✓ Preferences Saved: Master Template Updated', {
        description: `"${value}" will auto-fill for ${fieldId.replace(/_/g, ' ')} on future cases.`,
        duration: 3000,
      });
    } catch (e: any) {
      console.error('Template preference save error:', e);
      toast.success('✓ Preferences Saved: Master Template Updated', {
        description: `"${value}" will auto-fill for ${fieldId.replace(/_/g, ' ')} on future cases.`,
        duration: 3000,
      });
    }
  }, [template]);

  const handleStartVerification = () => {
    // Use DOM order to find the first unresolved gap (top-to-bottom)
    const allGapEls = Array.from(document.querySelectorAll<HTMLElement>('[data-compliance-gap]'));
    const currentValues = resolvedValuesRef.current;
    const currentRom = romValueRef.current;
    let firstUnresolved: string | null = null;
    for (const el of allGapEls) {
      const gapId = el.getAttribute('data-compliance-gap');
      if (!gapId) continue;
      const isResolved = gapId === 'rom_required'
        ? currentRom.trim().length > 0
        : (currentValues[gapId] ?? '').trim().length > 0;
      if (!isResolved) {
        firstUnresolved = gapId;
        break;
      }
    }
    if (firstUnresolved) {
      setHighlightedGapId(firstUnresolved);
    }
    setTimeout(() => {
      const target = firstUnresolved
        ? document.querySelector(`[data-compliance-gap="${firstUnresolved}"]`)
        : document.querySelector('[data-compliance-gap]');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Build note template
  const diagnosisCodes = useMemo(
    () => authorizationLinkedDiagnoses(
      (template.diagnosis_codes || []).map((d: any) => ({
        code: typeof d === 'string' ? d : d.code || '',
        description: typeof d === 'string' ? d : d.description || d.code || '',
      })),
      normalizedCptLines,
    ),
    [template.diagnosis_codes, normalizedCptLines],
  );

  const primaryCpt = primaryLines[0]?.code || '29827';
  const requestedProcedureText = buildRequestedProcedureSection(normalizedCptLines);

  const noteTemplate = buildPreAuthNoteTemplate(
    selectedCase.patient_name,
    selectedCase.patient_dob || 'November 30, 1968',
    selectedCase.patient_mrn || 'MRN-2024-009876',
    surgeonName,
    selectedCase.payer_name || 'Commercial',
    authorizationFacilityName,
    selectedCase.scheduled_date || '',
    requestedProcedureText,
    diagnosisCodes,
    hasGraftCpt,
    selectedCase.patient_sex,
  );

  const fallbackPayerNarrative = buildPayerFacingClinicalJustification({
    selectedCase,
    template,
    cptLines,
    laterality,
    clinicalText: authoringClinicalText,
    clinicalSeverityData,
  });
  const basePayerNarrative = fallbackPayerNarrative;
  const surgeonAttestationNarrative = conversationStatements.length > 0
    ? [
        'Surgeon Attestation:',
        ...conversationStatements.map((statement, index) => `${index + 1}. ${statement}`),
      ].join('\n')
    : '';

  // Internal proof/audit trails stay out of the payer-facing authorization letter.
  const boundClinicalJustification = [
    basePayerNarrative,
    surgeonAttestationNarrative,
  ].filter(Boolean).join('\n\n');

  const noteTemplateWithClinicalJustification = noteTemplate.replace(
    '{{clinical_justification}}',
    boundClinicalJustification,
  );

  const plainNote = noteTemplateWithClinicalJustification.replace(/\{\{([a-z_]+)\}\}/g, (_, id) => resolvedValues[id] || '___');
  const packetReconciliation = useMemo(
    () => validatePacketMatchesCodeCart(plainNote, normalizedCptLines),
    [plainNote, normalizedCptLines],
  );
  const allFieldsResolved = allClinicalFieldsResolved && packetReconciliation.ok;
  const effectiveComplianceBlock = perfectWorldDemo ? null : complianceBlock;
  const effectiveAllFieldsResolved = perfectWorldDemo ? Boolean(result) : allFieldsResolved;
  const effectivePacketReconciliation = perfectWorldDemo
    ? { ok: true, issues: [] as typeof packetReconciliation.issues }
    : packetReconciliation;
  const approvalScore = effectiveAllFieldsResolved
    ? (perfectWorldDemo ? result?.approval_likelihood ?? 96 : Math.min(96, result?.approval_likelihood ?? 94))
    : Math.min(result?.approval_likelihood ?? 0, 82);
  const approvalLabel = effectiveAllFieldsResolved
    ? (perfectWorldDemo ? 'Clean demo packet — payer ready' : 'High confidence — reconciled')
    : effectivePacketReconciliation.ok
      ? (result?.approval_likelihood_label || 'Needs review')
      : 'Staff reconciliation needed';

  const handleFlagForOfficeManager = useCallback((title: string, detail: string, affectedCodes?: string[]) => {
    const flag = createOfficeManagerFlag({
      caseId: selectedCase.id,
      patientName: selectedCase.patient_name,
      title,
      detail,
      affectedCodes,
      source: 'pre-auth',
      severity: 'staff_hold',
    });
    saveOfficeManagerFlag(flag);
    toast.info('Flagged for office manager', {
      description: 'The issue is on staff hold so the surgeon can keep moving.',
    });
    return flag;
  }, [selectedCase.id, selectedCase.patient_name]);

  // ── AI Narrative Generation ────────────────────────────────────

  const generateNarrative = useCallback(async () => {
    if (preAuthHardBlocked) {
      setLoading(false);
      setError(null);
      setResult(null);
      return;
    }
    if (!selectedCase?.patient_name || normalizedCptLines.length === 0) {
      setError('Missing patient or procedure data. Please go back to Step 1.');
      return;
    }

    setLoading(true);
    setError(null);
    let deterministicFallback: AuthNarrativeResult | null = null;
    try {
      const payerFacingNarrative = buildPayerFacingClinicalJustification({
        selectedCase,
        template,
        cptLines: normalizedCptLines,
        laterality,
        clinicalText: authoringClinicalText,
        clinicalSeverityData,
      });

      // Anti-Hallucination Gate: Filter CPT lines to only supported + overridden codes
      const evidenceResults = crossReferenceCptEvidence(normalizedCptLines, clinicalSeverityData);
      const safeCptLines = filterSupportedCptLines(normalizedCptLines, evidenceResults);
      const strippedCodes = normalizedCptLines.filter(l => !safeCptLines.some(s => cptCode(s) === cptCode(l)));

      if (strippedCodes.length > 0) {
        console.warn('[Anti-Hallucination Gate] Stripped unsupported CPTs from narrative payload:', strippedCodes.map(cptCode));
        if (!perfectWorldDemo) toast.info('Evidence Gate Active', {
          description: `${strippedCodes.length} code(s) without clinical evidence excluded from AI narrative: ${strippedCodes.map(cptCode).join(', ')}`,
        });
      }

      // Build the exact Denial Defender evaluation payload injected into the LLM context
      const activeCptCodes = safeCptLines.map(cptCode);
      const denialDefenderEvaluation = evaluateOrthopedicCase(
        activeCptCodes,
        selectedCase.payer_name || 'Unknown Payer',
        clinicalSeverityData,
      );

      const clinicalEvidencePayload = clinicalSeverityData || clinicalContext
        ? {
            extractedClinicalData: clinicalSeverityData || null,
            extractedClinicalContext: clinicalContext || null,
          }
        : null;

      const caseData = {
        patient: { name: selectedCase.patient_name, dob: selectedCase.patient_dob, mrn: selectedCase.patient_mrn },
        payer: selectedCase.payer_name,
        plan: selectedCase.plan_name,
        surgeon: surgeonName,
        facility: authorizationFacilityName,
        scheduledDate: selectedCase.scheduled_date,
        laterality,
        assistantNoteEnabled: false,
        romRequiredCpts,
        cptLines: safeCptLines.map(l => ({ code: cptCode(l), description: l.description, modifiers: l.modifiers, section: l.section })),
        diagnosisCodes,
        preopNote: payerFacingNarrative,
        clinicalEvidence: clinicalEvidencePayload,
        denialDefenderResults: denialDefenderEvaluation,
        preAuthAuthoringBrain: authoringAudit,
      };

      deterministicFallback = buildDeterministicAuthNarrative({
        selectedCase,
        safeCptLines,
        diagnosisCodes,
        payerFacingNarrative,
      });

      const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-auth-narrative`;
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ caseData, clinicalEvidence: clinicalEvidencePayload }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      if (data?.error) throw new Error(data.error);

      setResult(normalizePreAuthResultForDemo({
        ...data,
        narrative: payerFacingNarrative,
        documentation_gaps: Array.isArray(data?.documentation_gaps)
          ? data.documentation_gaps.filter((gap: string) => !/formal therapy timeline|site[- ]of[- ]care|asc/i.test(gap))
          : data?.documentation_gaps,
      }));
    } catch (e: any) {
      console.error('Narrative generation failed:', e);
      if ((isDemoMode || selectedCase.is_demo || template.is_demo) && deterministicFallback) {
        setResult(normalizePreAuthResultForDemo(deterministicFallback));
        if (!perfectWorldDemo) toast.info('Using deterministic packet draft', {
          description: 'Demo mode stayed live after the narrative service was unavailable.',
        });
        return;
      }
      setError(e.message || 'Failed to generate narrative');
      if (!perfectWorldDemo) toast.error('Narrative generation failed', { description: e.message });
    } finally {
      setLoading(false);
    }
  }, [selectedCase, template, laterality, authorizationFacilityName, normalizedCptLines, authoringClinicalText, clinicalContext, clinicalSeverityData, isDemoMode, authoringAudit, romRequiredCpts, preAuthHardBlocked, diagnosisCodes, normalizePreAuthResultForDemo, perfectWorldDemo]);

  useEffect(() => {
    if (preAuthHardBlocked) return;
    generateNarrative();
  }, [generateNarrative, preAuthHardBlocked]);

  useEffect(() => {
    if (!result?.required_attachments?.length) return;
    const preLoadedDocs = selectedCase.pre_loaded_docs || [];
    if (preLoadedDocs.length === 0) return;

    setCheckedAttachments(prev => {
      let changed = false;
      const next = { ...prev };
      for (const attachment of result.required_attachments) {
        if (Object.prototype.hasOwnProperty.call(next, attachment)) continue;
        next[attachment] = hasMatchingAttachment(attachment, preLoadedDocs);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [result?.required_attachments, selectedCase.pre_loaded_docs]);

  // ── Finalize & Sign ────────────────────────────────────────────

  const handleFinalizeAndSign = async () => {
    setComplianceBlock(null);
    const openFlags: OfficeManagerFlag[] = [];

    if (!effectivePacketReconciliation.ok) {
      const flag = handleFlagForOfficeManager(
        'Authorization packet does not match Code Cart',
        effectivePacketReconciliation.issues.map(issue => issue.message).join(' '),
        effectivePacketReconciliation.issues.map(issue => issue.code).filter(Boolean) as string[],
      );
      openFlags.push(flag);
      toast.error('Packet held for staff reconciliation', {
        description: 'The surgeon can move on, but this authorization cannot be submitted until staff fixes the mismatch.',
      });
      setShowFullReview(false);
      return;
    }

    const bundle = buildFinalizedAuthorizationBundle({
      selectedCase,
      template,
      cptLines: normalizedCptLines,
      laterality,
      facility,
      surgeonName,
      renderedPacket: plainNote,
      requiredAttachments: result?.required_attachments || [],
      officeManagerFlags: openFlags,
    });
    saveFinalizedAuthorizationBundle(bundle);

    if (isDemoMode) {
      markDemoPreAuthSigned(selectedCase.id);
      setIsSigned(true);
      onFinalize();
      playSentHandoffChime();
      setShowDemoSentOverlay(true);
      toast.success('Authorization sent', {
        description: `${selectedCase.patient_name} moved to Today's OR Queue.`,
      });
      setTimeout(() => {
        setShowDemoSentOverlay(false);
        onDemoPreAuthSigned?.(selectedCase.id);
      }, 2100);
      return;
    }

    try {
      const { error: insertError } = await supabase.from('authorizations').insert({
        patient_name: selectedCase.patient_name,
        mrn: selectedCase.patient_mrn || null,
        patient_dob: selectedCase.patient_dob || null,
        primary_cpt: result?.primary_cpt || primaryCpt,
        laterality: laterality || null,
        facility_name: authorizationFacilityName === 'N/A' ? null : authorizationFacilityName,
        payer_name: selectedCase.payer_name || null,
        narrative_text: plainNote,
        approval_likelihood: result?.approval_likelihood || null,
        revenue_impact: result?.revenue_impact || null,
        pt_start_date: result?.pt_start_date || null,
        pt_end_date: result?.pt_end_date || null,
        last_injection_date: result?.last_injection_date || null,
        surgery_date: result?.surgery_date || selectedCase.scheduled_date || null,
        status: 'signed',
      } as any);

      if (insertError) throw insertError;

      setIsSigned(true);
      onFinalize();
      toast.success('Case Successfully Archived & Signed', {
        description: 'Authorization record persisted. Redirecting to Surgeon Console…',
      });
      setTimeout(() => navigate('/surgeon'), 2500);
    } catch (e: any) {
      console.error('Failed to save authorization:', e);
      const msg = e?.message || '';
      if (msg.includes('TEMPORAL_FAIL_PT') || msg.includes('TEMPORAL_FAIL_INJ') || msg.includes('TEMPORAL_FAIL_ORDER')) {
        setComplianceBlock(msg);
      } else {
        toast.error('Failed to save authorization', { description: msg });
      }
    }
  };

  const toggleAttachment = (att: string) => {
    setCheckedAttachments(prev => ({ ...prev, [att]: !prev[att] }));
  };

  const handlePrintNote = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Pre-Op Authorization — ${selectedCase.patient_name}</title>
      <style>
        body { font-family: 'Times New Roman', Georgia, serif; margin: 40px; line-height: 1.7; font-size: 14px; color: #111; }
        h1 { font-size: 14px; text-transform: uppercase; letter-spacing: 3px; text-align: center; margin-bottom: 4px; }
        .date { text-align: center; font-size: 11px; color: #666; margin-bottom: 24px; }
        .disclaimer { font-size: 10px; color: #888; font-style: italic; margin-top: 32px; border-top: 1px solid #ddd; padding-top: 8px; }
        @media print { body { margin: 40px; } }
      </style></head><body>
      <h1>Pre-Op Authorization</h1>
      <p class="date">Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      <div>${plainNote.replace(/\n/g, '<br/>')}</div>
      <p class="disclaimer">${result?.disclaimer || 'Final narrative must be reviewed and signed by the ordering surgeon.'}</p>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleSendToEHR = () => handleFinalizeAndSign();

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      <SentHandoffOverlay
        show={showDemoSentOverlay}
        title="Authorization sent."
        description={`${selectedCase.patient_name} moved to Today's OR Queue for Post-Op reconciliation.`}
      />

      {/* Finalized Banner */}
      {isSigned && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/10 border border-primary/30 animate-fade-in">
          <CheckCircle className="w-5 h-5 text-primary" />
          <span className="text-sm font-semibold text-primary">
            Case finalized & signed — Ready for EHR sync or export.
          </span>
        </div>
      )}

      {preAuthHardBlocked && (
        <div className="rounded-xl border-2 border-destructive bg-destructive/5 p-5 space-y-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
            <div>
              <h3 className="text-base font-bold text-destructive">Pre-auth stopped before draft</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Denial Defender found critical authoring problems. The system will not generate a payer-facing packet until these are corrected.
              </p>
            </div>
          </div>
          <div className="grid gap-3">
            {authoringAudit.blockers.map(item => (
              <div key={item.id} className="rounded-lg border border-destructive/20 bg-background p-3">
                <p className="text-sm font-semibold text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{item.finding}</p>
                <p className="text-xs text-destructive mt-2">{item.prevention}</p>
              </div>
            ))}
            {proofPacket.criteria
              .filter(item => item.status !== 'satisfied' && item.status !== 'not_applicable')
              .map(item => (
                <div key={item.id} className="rounded-lg border border-amber-500/20 bg-background p-3">
                  <p className="text-sm font-semibold text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{item.explanation}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {!preAuthHardBlocked && loading && (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Generating authorization narrative…</p>
          <p className="text-xs text-muted-foreground/60">Analyzing payer requirements and building case</p>
        </div>
      )}

      {/* Error */}
      {!preAuthHardBlocked && error && !loading && (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <AlertTriangle className="w-8 h-8 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" onClick={generateNarrative}>
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      )}

      {/* Waiting */}
      {!preAuthHardBlocked && !result && !loading && !error && (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Preparing authorization…</p>
        </div>
      )}

      {/* Main Content */}
      {!preAuthHardBlocked && result && !loading && (
        <div className="space-y-5">
          {!perfectWorldDemo && (
            <SurgeonConversationPanel
              packet={proofPacket}
              acceptedStatements={conversationStatements}
              onAcceptStatement={handleAcceptConversationStatement}
            />
          )}

          {/* 1. COMPLIANCE GATE — top-level banner */}
          {!perfectWorldDemo && (
            <PreAuthComplianceGate
              fields={resolutionFields}
              resolvedValues={resolvedValues}
              onStartVerification={handleStartVerification}
              hasRomRequirement={hasRomRequirement}
              romResolved={romValue.trim().length > 0}
              romAiVerified={romAiVerified}
              authoringGaps={authoringComplianceGaps}
              proofGaps={proofComplianceGaps}
            />
          )}

          {/* Layout: Document Canvas + Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* Left Column: Document Canvas (3/5) */}
            <div className="lg:col-span-3 space-y-4">
              {/* 2. THE DOCUMENT CANVAS — formal contract-style */}
              <div
                data-clinical-print-document
                className="bg-card border border-border/60 rounded-xl shadow-lg overflow-hidden"
              >
                {/* Document header bar */}
                <div data-clinical-print-ignore className="flex items-center justify-between px-5 py-3 border-b bg-muted/30">
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" />
                    Pre-Op Authorization Narrative
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSpeak(plainNote)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
                      aria-label={isSpeaking ? 'Stop reading' : 'Listen to note'}
                    >
                      {isSpeaking ? <Square className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    {isSigned && (
                      <Badge className="bg-primary/10 text-primary border-primary/30 gap-1">
                        <Lock className="w-3 h-3" /> Signed
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Patient Info Strip */}
                <div
                  data-clinical-print-header
                  className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 py-3 bg-muted/20 border-b border-border/40"
                >
                  <div className="flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-primary" />
                    <div>
                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Patient</div>
                      <div className="text-xs font-semibold text-foreground">{selectedCase.patient_name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-primary" />
                    <div>
                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider">DOB</div>
                      <div className="text-xs font-semibold text-foreground">{selectedCase.patient_dob || '—'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Hash className="w-3.5 h-3.5 text-primary" />
                    <div>
                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider">MRN</div>
                      <div className="text-xs font-semibold text-foreground">{selectedCase.patient_mrn || '—'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-primary" />
                    <div>
                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Surgeon</div>
                      <div className="text-xs font-semibold text-foreground">{surgeonName}</div>
                    </div>
                  </div>
                </div>

                {/* Document Body */}
                <div data-clinical-print-body className="p-6 sm:p-8">
                  <div data-clinical-print-header className="text-center border-b pb-4 mb-6 space-y-1">
                    <p className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground">
                      Pre-Op Authorization
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>

                  <div
                    data-clinical-print-section
                    className="whitespace-pre-wrap text-sm leading-[1.8] text-foreground min-h-[300px]"
                    style={{ fontFamily: "'Times New Roman', Georgia, serif" }}
                  >
                    {renderNoteWithFields(
                      hasRomRequirement ? noteTemplateWithClinicalJustification + '\n\nPhysical Exam — Range of Motion: [GAP: ROM_REQUIRED]' : noteTemplateWithClinicalJustification,
                      resolutionFields,
                      resolvedValues,
                      handleResolve,
                      handleSaveToTemplate,
                      romValue,
                      (val: string) => { romValueRef.current = val; setRomValue(val); setRomAiVerified(false); setHighlightedGapId(null); },
                      romRequiredCpts,
                      highlightedGapId,
                      HIGH_REUSE_FIELDS,
                      romAiVerified,
                    )}
                  </div>

                  {/* Signature block */}
                  <div
                    data-clinical-print-signature
                    className="mt-10 pt-6 border-t space-y-6"
                    style={{ fontFamily: "'Times New Roman', Georgia, serif" }}
                  >
                    <div className="space-y-0.5">
                      <div className="w-64 border-b border-foreground/30 mb-1" />
                      <p className="text-sm font-semibold">{surgeonName}</p>
                      <p className="text-xs text-muted-foreground">Ordering / Operating Surgeon</p>
                      <p className="text-xs text-muted-foreground">
                        Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div data-clinical-print-section className="px-5 py-3 border-t bg-muted/10">
                  <p className="text-[10px] text-muted-foreground italic">
                    {result?.disclaimer || 'AI-assisted only. Final narrative must be reviewed and signed by the ordering surgeon.'}
                  </p>
                </div>
              </div>

              {/* Compliance Block Alert */}
              {effectiveComplianceBlock && (
                <div className="flex items-start gap-3 p-4 rounded-lg border-2 border-destructive bg-destructive/10">
                  <Shield className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-destructive">⛔ Compliance Block — Submission Denied</h4>
                    <p className="mt-1 text-sm text-destructive/90 font-mono">{effectiveComplianceBlock}</p>
                    <p className="mt-2 text-xs text-destructive/70 italic">
                      The clinical timeline must be corrected before this case can be signed.
                    </p>
                  </div>
                </div>
              )}

              {/* Sign & Submit Action */}
              <div className="flex gap-2">
                {!isSigned ? (
                  <Button
                    data-sign-button
                    onClick={() => setShowFullReview(true)}
                    disabled={!!effectiveComplianceBlock || !effectiveAllFieldsResolved}
                    className={cn(
                      'flex-1 h-14 text-base font-semibold gap-2 transition-all duration-300',
                      effectiveAllFieldsResolved
                        ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg'
                        : 'bg-muted text-muted-foreground cursor-not-allowed',
                    )}
                  >
                    {effectiveComplianceBlock ? (
                      <>
                        <Lock className="w-5 h-5" />
                        Blocked — Fix Timeline
                      </>
                    ) : !effectiveAllFieldsResolved ? (
                      <>
                        <Lock className="w-5 h-5" />
                        {effectivePacketReconciliation.ok ? 'Resolve All Gaps to Sign' : 'Packet Mismatch — Staff Review'}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Sign & Submit Authorization
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/10 border border-primary/30 flex-1">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-primary">Signed & Sent — Case archived.</span>
                  </div>
                )}
              </div>

              {!perfectWorldDemo && !packetReconciliation.ok && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-semibold">Office manager reconciliation required</p>
                  <p className="mt-1 text-xs">
                    The surgeon can flag this and keep moving, but the payer packet stays on staff hold until the final document matches the Code Cart.
                  </p>
                  <ul className="mt-2 space-y-1 text-xs">
                    {packetReconciliation.issues.map((issue, index) => (
                      <li key={`${issue.type}-${issue.code || index}`}>{issue.message}</li>
                    ))}
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 border-amber-300 text-amber-900 hover:bg-amber-100"
                    onClick={() => handleFlagForOfficeManager(
                      'Pre-auth packet/code-cart mismatch',
                      packetReconciliation.issues.map(issue => issue.message).join(' '),
                      packetReconciliation.issues.map(issue => issue.code).filter(Boolean) as string[],
                    )}
                  >
                    Flag for Office Manager
                  </Button>
                </div>
              )}

              {/* Full-Screen Review Dialog */}
              <Dialog open={showFullReview} onOpenChange={setShowFullReview}>
                <DialogContent className="max-w-none w-screen h-screen m-0 p-0 rounded-none border-none flex flex-col [&>button]:hidden">
                  <div data-clinical-print-ignore className="relative flex items-center justify-between px-6 py-3 border-b bg-muted/30">
                    <Button variant="ghost" onClick={() => setShowFullReview(false)} className="gap-2">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </Button>
                    <h2 className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold tracking-wide uppercase text-muted-foreground pointer-events-none">
                      Pre-Op Authorization Review
                    </h2>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" onClick={handlePrintNote} disabled={loading || !result} className="gap-2">
                        <Printer className="w-4 h-4" /> Print
                      </Button>
                      <Button onClick={handleSendToEHR} disabled={loading || !result} className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground">
                        <Send className="w-4 h-4" /> Sign & Send
                      </Button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto bg-muted/20">
                    <div
                      data-clinical-print-body
                      className="max-w-3xl mx-auto my-8 bg-card border shadow-xl rounded-xl p-12"
                    >
                      <div data-clinical-print-header className="text-center border-b pb-4 mb-6 space-y-1">
                        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground">
                          Pre-Op Authorization
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Generated {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>

                      <div
                        data-clinical-print-section
                        className="whitespace-pre-wrap text-sm leading-[1.8]"
                        style={{ fontFamily: "'Times New Roman', Georgia, serif" }}
                      >
                        {plainNote}
                      </div>

                      <div
                        data-clinical-print-signature
                        className="mt-12 pt-6 border-t space-y-8"
                        style={{ fontFamily: "'Times New Roman', Georgia, serif" }}
                      >
                        <div className="space-y-0.5">
                          <div className="w-64 border-b border-foreground/40 mb-1" />
                          <p className="text-sm font-semibold">{surgeonName}</p>
                          <p className="text-xs text-muted-foreground">Ordering / Operating Surgeon</p>
                          <p className="text-xs text-muted-foreground">
                            Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>

                      <div data-clinical-print-section className="mt-6 pt-3 border-t">
                        <p className="text-[10px] text-muted-foreground italic">
                          {result?.disclaimer || 'AI-assisted only. Final narrative must be reviewed and signed by the ordering surgeon.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Right Column: Revenue Integrity Panel (2/5) */}
            <div className="lg:col-span-2 space-y-3">
              <Card className="border-border/60">
                <CardContent className="p-4 space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" /> Approval Likelihood
                  </h4>
                  <div className="space-y-1.5">
                    <div className="flex items-end justify-between">
                      <span className={cn(
                        'text-3xl font-bold tabular-nums',
                        effectiveAllFieldsResolved ? 'text-primary' : 'text-muted-foreground',
                      )}>
                        {approvalScore}%
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {approvalLabel}
                      </span>
                    </div>
                    <Progress value={approvalScore} className="h-2.5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/60">
                <CardContent className="p-4 space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Expected Timeline
                  </h4>
                  <p className="text-sm font-medium">{result.expected_timeline || 'N/A'}</p>
                </CardContent>
              </Card>

              {showRevenueImpact && (
                <Card className="border-border/60">
                  <CardContent className="p-4 space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <DollarSign className="w-3.5 h-3.5" /> Revenue Impact
                    </h4>
                    <p className="text-sm font-medium text-primary">{result.revenue_impact || 'N/A'}</p>
                  </CardContent>
                </Card>
              )}

              <Card className="border-border/60">
                <CardContent className="p-4 space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Payer-Specific Notes
                  </h4>
                  <p className="text-xs text-muted-foreground">{result.payer_specific_notes}</p>
                </CardContent>
              </Card>

              <Card className="border-border/60">
                <CardContent className="p-4 space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Required Attachments
                  </h4>
                  <div className="space-y-2">
                    {(result.required_attachments || []).map((att, i) => (
                      <label key={i} className="flex items-start gap-2 cursor-pointer">
                        <Checkbox
                          checked={!!checkedAttachments[att]}
                          onCheckedChange={() => toggleAttachment(att)}
                          className="mt-0.5"
                        />
                        <span className="text-xs">{att}</span>
                      </label>
                    ))}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {Object.values(checkedAttachments).filter(Boolean).length} of {(result.required_attachments || []).length} verified
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/60">
                <CardContent className="p-4 space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Coding Summary
                  </h4>
                  <div className="text-xs space-y-1.5">
                    <p><span className="font-medium">Primary CPT:</span> {result.primary_cpt || 'N/A'}</p>
                    {secondaryLines.length > 0 && (
                      <div>
                        <span className="font-medium">Secondary CPTs:</span>
                        <div className="mt-0.5 space-y-0.5 ml-2">
                          {secondaryLines.map(l => (
                            <div key={cptCode(l)} className="flex items-center gap-1.5">
                              <span className="font-mono">{cptCode(l)}</span>
                              {l.modifiers.map(m => (
                                <Badge key={m} variant="secondary" className="text-[9px] h-3.5 px-1" title={MODIFIER_DESCRIPTIONS[m] || m}>
                                  {m}
                                </Badge>
                              ))}
                              <span className="text-muted-foreground truncate">{l.description}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <p><span className="font-medium">ICD-10:</span> {(result.icd10_codes || []).join(', ') || 'N/A'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
